import PdfPreview from "../ui/PdfPreview";
import type { FC } from "react";
import {
  FiChevronLeft,
  FiChevronRight,
  FiEdit2,
  FiTrash2,
} from "react-icons/fi";
import { SavedDesignCard } from "./DesignsSection";

export type SavedDesignListProps = {
  savedDesigns: SavedDesignCard[];
  hasReachedDesignLimit: boolean;
  onAddDesign: () => void;
  onDeleteAll: () => Promise<void>;
  onEditDesign: (design: SavedDesignCard) => void;
  onDeleteDesign: (id: string) => Promise<void>;
  onMovePreview: (designId: string, direction: "prev" | "next") => void;
  disabled?: boolean;
};

const SavedDesignList: FC<SavedDesignListProps> = ({
  savedDesigns,
  hasReachedDesignLimit,
  onAddDesign,
  onDeleteAll,
  onEditDesign,
  onDeleteDesign,
  onMovePreview,
  disabled = false,
}) => {
  return (
    <div>
      {savedDesigns.length > 0 && !disabled && (
        <div className="mb-5 flex flex-wrap items-center justify-end gap-6 text-sm font-semibold uppercase text-blue-800">
          <button
            type="button"
            onClick={onDeleteAll}
            className="hover:text-blue-700"
          >
            Delete all
          </button>
        </div>
      )}

      <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-4">
        {savedDesigns.map((design) => (
          <div
            key={design.id}
            className="overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm"
          >
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">
                  {design.name}
                </span>
                {!disabled && (
                  <button
                    type="button"
                    className="text-gray-500 hover:text-blue-800"
                    aria-label="Edit design"
                    onClick={() => onEditDesign(design)}
                  >
                    <FiEdit2 className="h-4 w-4" />
                  </button>
                )}
              </div>

              {!disabled && (
                <button
                  type="button"
                  onClick={() => onDeleteDesign(design.id)}
                  className="text-blue-800 hover:text-blue-700"
                  aria-label="Delete design"
                >
                  <FiTrash2 className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="relative flex h-44 items-center justify-center px-5 py-4">
              {design.previewUrls.length > 0 ? (
                <>
                  {design.previewUrls.length > 1 && (
                    <button
                      type="button"
                      onClick={() => onMovePreview(design.id, "prev")}
                      className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full border border-gray-200 bg-white p-1.5 text-gray-700 shadow-sm hover:text-blue-800"
                      aria-label="Previous view"
                    >
                      <FiChevronLeft className="h-4 w-4" />
                    </button>
                  )}

                  <img
                    src={
                      design.previewUrls[design.currentPreviewIndex] ??
                      design.previewUrls[0]
                    }
                    alt={design.name}
                    className="max-h-32 max-w-full object-contain"
                  />

                  {design.previewUrls.length > 1 && (
                    <button
                      type="button"
                      onClick={() => onMovePreview(design.id, "next")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full border border-gray-200 bg-white p-1.5 text-gray-700 shadow-sm hover:text-blue-800"
                      aria-label="Next view"
                    >
                      <FiChevronRight className="h-4 w-4" />
                    </button>
                  )}
                </>
              ) : (
                <span className="text-sm text-gray-500">No preview</span>
              )}
            </div>

            <div className="mx-4 mb-3 flex justify-center border-b border-gray-200 pb-3">
              <span className="rounded-full bg-black px-2 py-0.5 text-xs font-semibold text-white">
                {design.previewUrls.length
                  ? design.currentPreviewIndex + 1
                  : 0}{" "}
                / {design.viewCount}
              </span>
            </div>

            <div className="border-b border-gray-200 px-4 pb-3 text-sm font-semibold text-gray-600">
              {design.productLabel}
            </div>

            <div className="grid grid-cols-2 gap-2 px-4 py-3 text-xs font-semibold text-gray-600">
              <span>Deferment: {design.defermentLabel}</span>
              <span>Claims: {design.claimsCount}</span>
              {(design.payload?.priorities || []).map((priority: any, index: number) => (
                <div key={index} className="col-span-2 space-y-1">
                  {priority.copyFirstFilingDocument && <div>Priority {index + 1}: <PdfPreview documentId={priority.copyFirstFilingDocument} /></div>}
                  {priority.transferAssignmentDocument && <div>Transfer document: <PdfPreview documentId={priority.transferAssignmentDocument} /></div>}
                </div>
              ))}
            </div>
          </div>
        ))}

        <button
          type="button"
          onClick={onAddDesign}
          disabled={disabled || hasReachedDesignLimit}
          className={`flex min-h-[18rem] min-w-[260px] flex-col items-center justify-center gap-3 rounded-3xl border border-gray-200 bg-white px-8 py-10 text-center shadow-sm transition ${
            disabled || hasReachedDesignLimit
              ? "cursor-not-allowed opacity-50"
              : "hover:shadow-lg"
          }`}
        >
          <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-blue-800 text-white shadow-sm">
            <span className="-mt-1 text-4xl leading-none">+</span>
          </span>
          <span className="text-sm font-semibold uppercase tracking-[0.12em] text-blue-800">
            Add design
          </span>
        </button>
      </div>
    </div>
  );
};

export default SavedDesignList;
